/**
 * Authentication Provider Component
 * 
 * Provides authentication context to the entire application
 * Manages user state, tokens, and authentication methods
 * 
 * Requirements: 1.9, 1.10, 1.11
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  handleOAuthCallback,
  getStoredTokens,
  storeTokens,
  clearTokens,
  areTokensExpired,
  refreshTokens,
  redirectToLogin,
  redirectToLogout,
  isAdmin as checkIsAdmin,
  isCoach as checkIsCoach,
} from './cognito';
import { restClient, User } from '../api/rest-client';

interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface AuthContextType {
  user: User | null;
  tokens: CognitoTokens | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCoach: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<CognitoTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch full user information from API
   */
  const fetchUserFromAPI = async (): Promise<User | null> => {
    try {
      const userData = await restClient.getCurrentUser();
      return userData;
    } catch (error) {
      console.error('[AuthProvider] Failed to fetch user from API:', error);
      return null;
    }
  };

  /**
   * Initialize authentication state
   */
  const initializeAuth = async () => {
    // Check if logout is in progress
    const logoutInProgress = sessionStorage.getItem('logout_in_progress');
    if (logoutInProgress) {
      console.log('[Auth] Logout in progress, skipping auto-login');
      sessionStorage.removeItem('logout_in_progress');
      clearTokens(); // Ensure tokens are cleared
      setIsLoading(false);
      return;
    }
    
    // Check for OAuth callback (now async for Authorization Code flow)
    const callbackTokens = await handleOAuthCallback();
    if (callbackTokens) {
      setTokens(callbackTokens);
      
      // Fetch full user data from API
      const userData = await fetchUserFromAPI();
      setUser(userData);
      setIsLoading(false);
      
      // Prefetch users list in background for better UX
      prefetchUsers();
      return;
    }

    // Check for stored tokens
    const storedTokens = getStoredTokens();
    if (storedTokens) {
      // Check if tokens are expired
      if (areTokensExpired(storedTokens)) {
        // Try to refresh
        const newTokens = await refreshTokens(storedTokens);
        if (newTokens) {
          storeTokens(newTokens);
          setTokens(newTokens);
          
          // Fetch full user data from API
          const userData = await fetchUserFromAPI();
          setUser(userData);
          
          // Prefetch users list in background for better UX
          prefetchUsers();
        } else {
          // Refresh failed, clear tokens
          clearTokens();
          setTokens(null);
          setUser(null);
        }
        setIsLoading(false);
      } else {
        // Tokens are valid
        setTokens(storedTokens);
        
        // Fetch full user data from API
        const userData = await fetchUserFromAPI();
        setUser(userData);
        setIsLoading(false);
        
        // Prefetch users list in background for better UX
        prefetchUsers();
      }
    } else {
      // No tokens found
      setIsLoading(false);
    }
  };

  /**
   * Prefetch users list in background
   * This improves UX by loading users before they're needed
   * Only admins have access to the users endpoint
   */
  const prefetchUsers = () => {
    // Only prefetch for admins
    if (!user || !user.groups || !user.groups.includes('admin')) {
      return;
    }
    
    // Fire and forget - load users in background
    restClient.getUsers().catch((err) => {
      // Silently fail - users will be fetched again when needed
      console.debug('[Auth] Background user prefetch failed:', err);
    });
  };

  /**
   * Restore tokens from localStorage when app becomes visible
   * This handles mobile Safari clearing sessionStorage when app is backgrounded
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      console.log('[Auth] App became visible, checking token restoration');
      
      // Check if sessionStorage has tokens
      const sessionTokens = sessionStorage.getItem('cognito_tokens');
      
      // If no tokens in sessionStorage, try to restore from localStorage
      if (!sessionTokens) {
        const localTokens = localStorage.getItem('cognito_tokens');
        if (localTokens) {
          console.log('[Auth] Restoring tokens from localStorage to sessionStorage');
          sessionStorage.setItem('cognito_tokens', localTokens);
          
          // Update state if we don't have tokens loaded
          if (!tokens) {
            try {
              const parsedTokens = JSON.parse(localTokens);
              setTokens(parsedTokens);
              
              // Fetch user data if we don't have it
              if (!user) {
                fetchUserFromAPI().then(setUser);
              }
            } catch (error) {
              console.error('[Auth] Failed to parse restored tokens:', error);
            }
          }
        }
      }
    }
  };

  /**
   * Initialize on mount and set up visibility listener
   */
  useEffect(() => {
    initializeAuth();
    
    // Add visibility change listener for mobile Safari session persistence
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /**
   * Login - redirect to Cognito
   */
  const login = () => {
    redirectToLogin();
  };

  /**
   * Logout - clear tokens and redirect
   */
  const logout = () => {
    console.log('[Auth] Logging out - clearing all tokens and state');
    
    // Clear tokens from storage first
    clearTokens();
    
    // Clear state
    setTokens(null);
    setUser(null);
    
    // Set a flag to prevent auto-login after logout redirect
    sessionStorage.setItem('logout_in_progress', 'true');
    
    // Small delay to ensure storage is cleared before redirect
    setTimeout(() => {
      redirectToLogout();
    }, 100);
  };

  /**
   * Refresh authentication (check and refresh tokens if needed)
   */
  const refreshAuth = async () => {
    if (!tokens) {
      return;
    }

    if (areTokensExpired(tokens)) {
      const newTokens = await refreshTokens(tokens);
      if (newTokens) {
        storeTokens(newTokens);
        setTokens(newTokens);
        
        // Fetch full user data from API
        const userData = await fetchUserFromAPI();
        setUser(userData);
      } else {
        // Refresh failed, logout
        logout();
      }
    }
  };

  /**
   * Re-fetch user data after a profile update.
   *
   * Because getCurrentUser() reads claims from the JWT id-token,
   * we must force a token refresh first so Cognito returns a new
   * id-token that contains the updated attributes (e.g. phone_number).
   */
  const refreshUser = async () => {
    if (tokens && tokens.refreshToken) {
      const newTokens = await refreshTokens(tokens);
      if (newTokens) {
        storeTokens(newTokens);
        setTokens(newTokens);
      }
    }
    const userData = await fetchUserFromAPI();
    if (userData) {
      setUser(userData);
    }
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = !!user && !!tokens && !areTokensExpired(tokens);

  /**
   * Check if user is admin
   */
  const isAdmin = user ? checkIsAdmin(user.groups) : false;

  /**
   * Check if user is coach
   */
  const isCoach = user ? checkIsCoach(user.groups) : false;

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated,
    isAdmin,
    isCoach,
    isLoading,
    login,
    logout,
    refreshAuth,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
